import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import MinimalLayout from '../components/layout/MinimalLayout';
import PaymentForm from '../components/payment/PaymentForm';
import { stripePromise, STRIPE_CONFIG, createPaymentIntent } from '../config/stripe';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CreditCardIcon
} from '@heroicons/react/24/solid';

interface PaymentPageData {
  teacher: any;
  bookingData: any;
  studentData?: any;
  lessonRequest?: any;
}

const PaymentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const {
    teacher,
    bookingData,
    studentData,
    lessonRequest
  }: PaymentPageData = location.state || {};

  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'processing' | 'success' | 'error'>('pending');
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Log payment page access
  console.log('Payment page loaded');
  console.log('Teacher:', teacher);
  console.log('Booking data:', bookingData);
  console.log('Student data:', studentData);
  console.log('Lesson request:', lessonRequest);

  // Redirect if missing required data
  useEffect(() => {
    if (!teacher || !bookingData) {
      console.warn('Missing payment data, redirecting to browse teachers');
      navigate('/browse-teachers');
      return;
    }

    // Create payment intent with real API
    const createRealPaymentIntent = async () => {
      console.log('Creating real payment intent for lesson');
      setIsCreatingPaymentIntent(true);
      setError(null);
      
      try {
        const amount = calculateAmount();
        const paymentData = {
          amount: amount,
          lessonDetails: {
            teacherName: teacher.name,
            instrument: bookingData.instrument || teacher.instruments?.[0] || 'Music',
            date: bookingData.selectedDate || 'TBD',
            time: bookingData.selectedTime || 'TBD',
            duration: bookingData.duration || 60,
            lessonType: bookingData.lessonType || 'Online'
          },
          studentData: studentData || {
            firstName: 'Demo',
            lastName: 'Student',
            email: 'demo@lessonlynx.com'
          },
          teacherData: teacher,
          lessonId: lessonRequest?.id || null
        };

        const result = await createPaymentIntent(paymentData);
        setClientSecret(result.clientSecret);
        
        console.log('Payment intent created successfully:', result.paymentIntentId);
        
      } catch (error: any) {
        console.error('Failed to create payment intent:', error);
        setError(error.message || 'Failed to initialize payment. Please try again.');
      } finally {
        setIsCreatingPaymentIntent(false);
      }
    };

    createRealPaymentIntent();
  }, [teacher, bookingData, studentData, lessonRequest, navigate]);

  const calculateAmount = () => {
    if (!bookingData || !teacher) return 0;
    
    const hourlyRate = teacher.hourlyRate || 75;
    const duration = bookingData.duration || 60;
    
    // Calculate price based on duration
    if (duration === 30) {
      return hourlyRate / 2;
    } else if (duration === 45) {
      return (hourlyRate * 3) / 4;
    } else {
      return hourlyRate;
    }
  };

  const handlePaymentSuccess = (paymentIntent: any) => {
    console.log('Payment successful:', paymentIntent);
    setPaymentStatus('success');
    setPaymentDetails(paymentIntent);
    
    // After 2 seconds, redirect to success page
    setTimeout(() => {
      navigate('/payment-success', {
        state: {
          paymentIntent,
          teacher,
          bookingData,
          studentData,
          lessonRequest
        }
      });
    }, 2000);
  };

  const handlePaymentError = (error: string) => {
    console.error('Payment failed:', error);
    setPaymentStatus('error');
    setError(error);
  };

  const handleGoBack = () => {
    console.log('Going back to booking confirmation');
    navigate(-1);
  };

  if (!teacher || !bookingData) {
    return null;
  }

  const amount = calculateAmount();
  const lessonDetails = {
    teacherName: teacher.name,
    instrument: bookingData.instrument || teacher.instruments?.[0] || 'Music',
    date: bookingData.selectedDate || 'TBD',
    time: bookingData.selectedTime || 'TBD',
    duration: bookingData.duration || 60,
    lessonType: bookingData.lessonType || 'Online'
  };

  // Elements options
  const elementsOptions = {
    appearance: STRIPE_CONFIG.appearance,
    clientSecret: clientSecret || undefined
  };

  if (paymentStatus === 'success') {
    return (
      <MinimalLayout noPadding>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md w-full mx-4">
            <div className="bg-white rounded-lg shadow-lg border p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-6">
                Your payment has been processed. You'll be redirected to the confirmation page shortly.
              </p>
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          </div>
        </div>
      </MinimalLayout>
    );
  }

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleGoBack}
                  className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <div className="flex items-center space-x-3">
                  <CreditCardIcon className="w-6 h-6 text-blue-600" />
                  <h1 className="text-xl font-semibold text-gray-900">Secure Payment</h1>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                Step 3 of 3
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column - Payment Form */}
            <div>
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="text-red-500">⚠️</div>
                    <div>
                      <h4 className="text-sm font-medium text-red-800">Payment Error</h4>
                      <p className="text-sm text-red-700 mt-1">{error}</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {isCreatingPaymentIntent ? (
                <div className="bg-white rounded-lg shadow-lg border p-6">
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-600">Preparing secure payment...</p>
                    </div>
                  </div>
                </div>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={elementsOptions}>
                  <PaymentForm
                    amount={amount}
                    lessonDetails={lessonDetails}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                </Elements>
              ) : (
                <div className="bg-white rounded-lg shadow-lg border p-6">
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <div className="text-red-500 text-4xl mb-4">⚠️</div>
                      <p className="text-gray-600">Unable to initialize payment</p>
                      <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Order Summary */}
            <div className="space-y-6">
              {/* Order Summary */}
              <div className="bg-white rounded-lg shadow border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-lg">🎵</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{lessonDetails.instrument} Lesson</h4>
                      <p className="text-sm text-gray-600">with {lessonDetails.teacherName}</p>
                      <p className="text-sm text-gray-600">{lessonDetails.duration} minutes • {lessonDetails.lessonType}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">${amount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 mt-4 pt-4">
                  <div className="flex justify-between">
                    <span className="text-base font-medium text-gray-900">Total</span>
                    <span className="text-lg font-bold text-gray-900">${amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Security Features */}
              <div className="bg-white rounded-lg shadow border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Security & Trust</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Stripe Secured</p>
                      <p className="text-sm text-gray-600">Industry-leading payment security</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Real-time Processing</p>
                      <p className="text-sm text-gray-600">Connected to live payment system</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Money-Back Guarantee</p>
                      <p className="text-sm text-gray-600">Full refund if not satisfied</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <div className="text-center">
                  <p className="text-sm text-blue-800 font-medium">Need Help?</p>
                  <p className="text-sm text-blue-700">
                    Contact our support team at{' '}
                    <a href="mailto:support@lessonlynx.com" className="underline">
                      support@lessonlynx.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default PaymentPage; 